import { Divider } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useCallback, useState } from 'react'

import { Loading } from '@/components'

import { DashboardContent } from '../components/DashboardContent'
import { PrivateRegionViewSwitcher } from '../components/PrivateRegionViewSwitcher'
import { RegionSelector } from '../components/RegionSelector'
import {
  useChartUsageMetrics,
  useDashboardData,
  useDomainSelection,
  useUsageMetrics,
} from '../hooks'

dayjs.extend(utc)

type UsageTimeRangeType = 'this_month' | 'last_3_months' | 'last_6_months' | 'last_12_months'

export function DashboardView() {
  const [usageTimeRange, setUsageTimeRange] = useState<UsageTimeRangeType>('this_month')

  // Domain selection
  const {
    domainInfos,
    selectedRegion,
    setSelectedRegion,
    loading: domainLoading,
  } = useDomainSelection()

  // Dashboard data
  const {
    regionInfo,
    overviewData,
    gpuBriefData,
    loading: dashboardLoading,
  } = useDashboardData(selectedRegion, domainInfos, undefined)

  // Chart data (dynamic time range)
  const { chartUsageMetrics, loading: chartLoading } = useChartUsageMetrics(
    selectedRegion,
    usageTimeRange,
    undefined, // No model filter in dashboard yet
  )

  // Usage metrics data (fixed "this month" for cards)
  const { usageMetrics } = useUsageMetrics(selectedRegion, undefined)

  // Get display text for usage time range (local timezone)
  const getUsageTimeRangeText = useCallback(() => {
    const now = dayjs()

    switch (usageTimeRange) {
      case 'this_month':
        return now.format('YYYY/MM')
      case 'last_3_months': {
        const start = now.subtract(2, 'month')
        return `${start.format('YYYY/MM')} - ${now.format('YYYY/MM')}`
      }
      case 'last_6_months': {
        const start = now.subtract(5, 'month')
        return `${start.format('YYYY/MM')} - ${now.format('YYYY/MM')}`
      }
      case 'last_12_months': {
        const start = now.subtract(11, 'month')
        return `${start.format('YYYY/MM')} - ${now.format('YYYY/MM')}`
      }
    }
  }, [usageTimeRange])

  // Determine loading state - only show full page loading for initial data
  const initialLoading = domainLoading || dashboardLoading

  return (
    <div className="card bg-panel-white h-full overflow-y-auto">
      <div className="">
        <div className="flex items-center justify-between">
          <PrivateRegionViewSwitcher />
          <RegionSelector
            domainInfos={domainInfos}
            selectedRegion={selectedRegion}
            setSelectedRegion={setSelectedRegion}
          />
        </div>

        <Divider className="my-8" />

        {/* Content */}
        {initialLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loading />
          </div>
        ) : (
          <DashboardContent
            regionInfo={regionInfo}
            overviewData={overviewData}
            gpuBriefData={gpuBriefData}
            usageMetrics={usageMetrics}
            chartUsageMetrics={chartUsageMetrics}
            chartLoading={chartLoading}
            usageTimeRange={usageTimeRange}
            onUsageTimeRangeChange={setUsageTimeRange}
            getUsageTimeRangeText={getUsageTimeRangeText}
          />
        )}
      </div>
    </div>
  )
}
